import {
  Button,
  Modal,
  Form,
  Container,
  Row,
  Col,
  InputGroup,
} from "react-bootstrap";
import { ClipboardEvent, SyntheticEvent, useEffect, useState } from "react";
import { Problem, ValidationProblem, formErrors } from "../api/backendApi";
import FormErrorDisplay from "../common/FormErrorDisplay";
import { VinModel, vinAPI } from "../api/vinAPI";
import ErrorDisplay from "../common/ErrorDisplay";
import { Trash, TrashFill } from "react-bootstrap-icons";

interface VinModalProps {
  recId: number;
  onCancel: () => void;
  modalText: string;
  disabled: boolean;
}

export function VinModal({
  recId,
  onCancel,
  modalText,
  disabled,
}: VinModalProps) {
  const [modalTitle, setModalTitle] = useState(modalText);
  const [editVins, setEditVins] = useState<VinModel[]>([
    {
      vin: "",
    },
  ]);
  const [vins, setVins] = useState<VinModel[]>([]);
  const [editVin, setEditVin] = useState<VinModel | undefined>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Problem | undefined>(undefined);
  const [errors, setErrors] = useState<formErrors>({});
  const [isEditing, setIsEditing] = useState(false);
  const [submitDisabled, setSubmitDisabled] = useState(false);
  const [submitQueue, setSubmitQueue] = useState<VinModel[]>([]);

  useEffect(() => {
    async function loadModal(recId: number) {
      loadVins(recId);
    }
    loadModal(recId);
  }, [recId]);

  const loadVins = async (recId: number) => {
    setLoading(true);
    const data = await vinAPI.getVins(recId);

    if (data.ok) {
      setError(undefined);
      setVins(data.value);
    } else {
      setError(data.problem);
    }

    setLoading(false);
  };

  const deleteVin = async (recId: number, vin: VinModel) => {
    setLoading(true);

    const result = await vinAPI.deleteVin(recId, vin);

    if (result.ok) {
      setErrors({});
      await loadVins(recId);
    } else {
      setLoading(false);
    }
  };

  useEffect(() => {
    async function submitVin() {
      // don't kill the server
      await new Promise((f) => setTimeout(f, 50));
      const result = await (isEditing && editVin
        ? vinAPI.updateVin(recId, editVin.vin ?? "", submitQueue[0])
        : vinAPI.createVin(recId, submitQueue[0] ?? {}));

      if (result.ok) {
        const newQueue = submitQueue.slice(1);
        setSubmitQueue(newQueue);
        if (newQueue.length === 0) {
          setErrors({});
          await loadVins(recId);
          setIsEditing(false);
          setEditVin(undefined);
          setModalTitle(modalText);
          setEditVins([{ vin: "" }]);
        } else {
          // remove first element, we are done with it
          setEditVins(editVins.slice(1));
        }
      } else {
        setSubmitQueue([]);
        let validation = result.problem as ValidationProblem;
        if (validation.validation) {
          setErrors(validation.validation);
        } else {
          let errors: formErrors = {};
          errors[""] = [`${result.problem.title} [${result.problem.instance}]`];
          setErrors(errors);
        }
      }
    }

    if (submitQueue.length > 0) {
      submitVin();
    } else {
      setSubmitDisabled(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submitQueue]);

  const handleSubmit = async (event: SyntheticEvent) => {
    event.preventDefault();

    setSubmitDisabled(true);
    setSubmitQueue(editVins.slice());
  };

  const pasteVins = (
    event: ClipboardEvent<HTMLInputElement | HTMLTextAreaElement>,
    v: VinModel,
  ) => {
    // default behavior if the textbox already has data
    if (event.currentTarget.value) return;

    // not allowed when editing
    if (isEditing) return;

    const text = event.clipboardData.getData("text");

    if (text) {
      const entries: VinModel[] = text
        .split(/[\s,;]+/)
        .map((t) => t.trim())
        .filter((t) => t)
        .map((t) => ({ vin: t }));

      const index = editVins.indexOf(v);

      if (index >= 0) {
        let modified: VinModel[] = [];

        if (index > 0) {
          modified = editVins.slice(0, index - 1);
        }

        modified = modified.concat(entries.concat(editVins.slice(index + 1)));

        setEditVins(modified);
      }
    }

    event.preventDefault();
  };

  return (
    <>
      <Modal onHide={() => onCancel()} show={true} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{modalTitle}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Container>
            <FormErrorDisplay errors={errors[""]} />
            <Row>
              {!disabled && (
                <Col>
                  <Form.Label htmlFor="vin0">VIN</Form.Label>
                  <Form
                    onSubmit={handleSubmit}
                    id="vinForm"
                    style={{ maxHeight: "250px", overflow: "auto" }}
                  >
                    {editVins.map((v, index) => (
                      <InputGroup key={index} className="mb-1" size="sm">
                        <Form.Control
                          name={"vin" + index}
                          type="text"
                          id={"vin" + index}
                          placeholder={"VIN..."}
                          value={v.vin}
                          disabled={submitDisabled}
                          onChange={(event) =>
                            setEditVins(
                              editVins.map((editVin) =>
                                editVin === v
                                  ? { vin: event.target.value }
                                  : editVin,
                              ),
                            )
                          }
                          onPaste={(event) => {
                            pasteVins(event, v);
                          }}
                        />
                        {index === 0 && (
                          <FormErrorDisplay errors={errors.vin} />
                        )}
                        {editVins.length > 1 && (
                          <Button
                            variant="outline-danger"
                            onClick={() =>
                              setEditVins(editVins.filter((x) => x !== v))
                            }
                          >
                            <Trash size="12" />
                          </Button>
                        )}
                      </InputGroup>
                    ))}
                  </Form>
                  {isEditing && (
                    <>
                      <Button
                        variant="primary"
                        className="mt-3"
                        onClick={handleSubmit}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="secondary"
                        className="mt-3 ms-3"
                        onClick={() => {
                          setErrors({});
                          setIsEditing(false);
                          setEditVin(undefined);
                          setEditVins([
                            {
                              vin: "",
                            },
                          ]);
                          setModalTitle(modalText);
                        }}
                      >
                        Cancel
                      </Button>
                    </>
                  )}
                  {!isEditing && (
                    <Button
                      variant="primary"
                      form="vinForm"
                      type="submit"
                      className="mt-3"
                      disabled={submitDisabled}
                    >
                      Add{editVins.length > 1 && ` (${editVins.length})`}
                    </Button>
                  )}
                </Col>
              )}
              <Col>
                <div className="fixed-table-header">
                  <table className="table table-hover">
                    <thead>
                      <tr>
                        <th>VIN</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading && (
                        <tr>
                          <td colSpan={2}>Loading...</td>
                        </tr>
                      )}

                      {error && (
                        <tr>
                          <td colSpan={2}>
                            <ErrorDisplay error={error}></ErrorDisplay>
                          </td>
                        </tr>
                      )}
                      {!loading &&
                        !error &&
                        (vins.length === 0 ? (
                          <tr>
                            <td colSpan={2}>No VINs...</td>
                          </tr>
                        ) : (
                          vins.map((x) => (
                            <tr
                              key={x.vin}
                              onClick={() => {
                                if (!disabled) {
                                  setErrors({});
                                  setEditVin(x);
                                  setModalTitle(`Edit VIN ${x.vin}`);
                                  setIsEditing(true);
                                  setEditVins([x]);
                                }
                              }}
                            >
                              <td>{x.vin}</td>
                              <td className="text-end">
                                <TrashFill
                                  className="text-danger"
                                  height={24}
                                  width={24}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (
                                      window.confirm(
                                        "Are you sure you want to delete this VIN?",
                                      )
                                    ) {
                                      deleteVin(recId, x);
                                    }
                                  }}
                                ></TrashFill>
                              </td>
                            </tr>
                          ))
                        ))}
                    </tbody>
                  </table>
                </div>
              </Col>
            </Row>
          </Container>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onCancel}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}
